import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import EntityEmailSyncStatus from "@mapmycustomers/shared/enum/EntityEmailSyncStatus";
import EntityEmailSync from "@mapmycustomers/shared/types/email/EntityEmailSync";
import { EntitiesSupportedByEmailFeature } from "@mapmycustomers/shared/types/map/types";
import { Alert } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import { dismissEntityEmailsSync, fetchEntityEmailSync } from "../store/actions";
import { getEmailThreadsEntity, getEntityEmailSync } from "../store/selectors";
import canSyncEntityEmails from "../util/canSyncEntityEmails";

import messages from "./messages";

const REFRESH_INTERVAL = 60 * 1000;

interface EmailSyncAlertProps {
  entity: EntitiesSupportedByEmailFeature;
  entityEmailSync?: EntityEmailSync;
  onDismiss: typeof dismissEntityEmailsSync.request;
  onFetchEntityEmailSync: typeof fetchEntityEmailSync.request;
}

const EmailSyncAlert: React.FC<EmailSyncAlertProps> = ({
  entity,
  entityEmailSync,
  onDismiss,
  onFetchEntityEmailSync,
}) => {
  const intl = useIntl();

  const syncFailed = useMemo(() => {
    return entityEmailSync?.status === EntityEmailSyncStatus.ERRORED;
  }, [entityEmailSync]);

  const syncInProgress = useMemo(() => {
    return (
      entityEmailSync &&
      !entityEmailSync.isDismissed &&
      [EntityEmailSyncStatus.QUEUED, EntityEmailSyncStatus.RUNNING].includes(entityEmailSync.status)
    );
  }, [entityEmailSync]);

  useEffect(() => {
    if (canSyncEntityEmails(entity)) {
      onFetchEntityEmailSync();
    }
  }, [entity, onFetchEntityEmailSync]);

  useEffect(() => {
    if (!syncInProgress) {
      return;
    }

    const intervalId = setInterval(() => {
      onFetchEntityEmailSync();
    }, REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [syncInProgress, onFetchEntityEmailSync]);

  const handleAlertClose = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  if (syncFailed) {
    return (
      <Alert message={intl.formatMessage(messages.syncFailedAlertMessage)} showIcon type="error" />
    );
  }

  if (syncInProgress) {
    return (
      <Alert
        closable
        message={intl.formatMessage(messages.syncInProgressAlertMessage)}
        onClose={handleAlertClose}
        showIcon
        type="info"
      />
    );
  }

  return null;
};

const mapStateToProps = (state: RootState) => ({
  entity: getEmailThreadsEntity(state)!,
  entityEmailSync: getEntityEmailSync(state),
});

const mapDispatchToProps = {
  onDismiss: dismissEntityEmailsSync.request,
  onFetchEntityEmailSync: fetchEntityEmailSync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailSyncAlert);
